import React, { useState, useEffect } from 'react';
import { FiSearch } from 'react-icons/fi';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { RootState, AppDispatch } from '../../app/store';
import { PrebuiltTemplate } from '../../features/templates/types';
import { setCurrentTemplateId } from '../../features/templates/templatesSlice';
import fetchPrebuiltTemplates from '../../features/templates/prebuiltTemplatesApi';

const FiSearchComponent = FiSearch as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

interface PrebuiltTemplatesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrebuiltTemplatesModal: React.FC<PrebuiltTemplatesModalProps> = ({
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Get prebuilt templates from Redux
  const prebuiltTemplates = useSelector(
    (state: RootState) => state.templates.prebuiltTemplates as PrebuiltTemplate[]
  );
  const prebuiltStatus = useSelector(
    (state: RootState) => state.templates.prebuiltStatus
  );

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch prebuilt templates when modal opens or search changes
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchPrebuiltTemplates({
        orgId: 'org_123456',
        name: debouncedSearch,
      }));
    }
  }, [isOpen, debouncedSearch, dispatch]);

  if (!isOpen) return null;

  const handleEdit = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    onClose();
    localStorage.setItem('currentTemplateId', id);
    dispatch(setCurrentTemplateId(id));
    navigate('/templates/editor', {
      state: { fromTab2: 'templatesGallery' },
    });
  };

  // Filter templates by search query (already filtered by API, but keep for safety)
  const filteredTemplates = prebuiltTemplates
    .filter((t) => t.name.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <div className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50'>
      <div className='bg-white rounded-lg shadow-lg w-[600px] h-[80vh] flex flex-col'>
        {/* Header */}
        <div className='flex justify-between items-start p-6 pb-4'>
          <h2 className='text-[20px] font-semibold text-[#3F5265]'>
            Quick Start with Pre-built Templates
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            ✖
          </button>
        </div>

        {/* Search Bar - stays on top */}
        <div className='px-6 pb-4'>
          <div className='relative'>
            <input
              type='text'
              placeholder='Search templates'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className='w-full border rounded-md px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500'
            />
            <FiSearchComponent className='absolute right-3 top-3 text-gray-400' />
          </div>
        </div>

        {/* Scrollable Cards Area - this is the only part that scrolls */}
        <div className='flex-1 overflow-y-auto px-6 pb-6'>
          {prebuiltStatus === 'loading' ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-orange-500"></div>
            </div>
          ) : filteredTemplates.length > 0 ? (
            <div className='grid grid-cols-2 gap-4'>
              {filteredTemplates.map((t) => (
                <div
                  key={t.id}
                  className='bg-white border rounded-md shadow py-3 pt-0 relative'
                >
                  {t.thumbnail ? (
                    <img
                      src={t.thumbnail}
                      alt={t.name}
                      className='w-full h-40 object-cover rounded-t-md'
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className='w-full h-40 bg-gray-100 rounded-t-md flex items-center justify-center'>
                      <span className='text-gray-400 text-sm'>Template</span>
                    </div>
                  )}
                  <p
                    className='text-sm text-gray-700 mt-2 px-3'
                    style={{ color: '#3F5265', fontSize: '14px' }}
                  >
                    {t.name}
                  </p>
                  <div className='absolute top-2 right-2'>
                    <button
                      onClick={(e) => handleEdit(e, t.id)}
                      className='px-2 py-1 border rounded text-sm bg-white hover:bg-gray-50'
                    >
                      Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64">
              <p className='text-gray-500 text-center'>
                {searchQuery ? 'No templates found matching your search' : 'No prebuilt templates available'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrebuiltTemplatesModal;